import { useEffect } from 'react';
import { useAuthStore } from 'src/store/auth';
import { useApplicationStore } from 'src/store/application';
import { useHealthStore } from 'src/store/health';
import { useInsuranceStore } from 'src/store/insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { transformApplicationToOnboardingData } from '@features/onboarding/utils/transformApplicationToOnboardingData';
import { useAI } from 'src/hooks/useAI';
import { Application } from 'src/services/interfaces/application';

export const useLayoutProviderData = () => {
  const { isAuthenticated } = useAuthStore();
  const {
    setData: setOnboardingData,
    isLoaded,
    setCurrentStep,
    resetAllForms,
    enhanceHealthQuestionsData,
    data,
  } = useOnboardingStore();
  const { applications, updateTierFromApplicationState } = useApplicationStore();
  const { getConditions, getIssueDetails, getIssues, issues, conditions } = useHealthStore();
  const { plans: insurancePlans, getFullPlans, getProviders } = useInsuranceStore();

  // Initialize useAI for handling hooks
  useAI();

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      getConditions();
      getIssueDetails();
      getIssues();
      getProviders();
      getFullPlans();
    }
  }, [isAuthenticated, getConditions, getFullPlans, getProviders, getIssueDetails, getIssues]);

  // Update tier when insurance plans are loaded
  useEffect(() => {
    if (insurancePlans.length > 0) {
      updateTierFromApplicationState();
    }
  }, [insurancePlans.length, updateTierFromApplicationState]);

  // Transform and set onboarding data when applications and plans are available
  useEffect(() => {
    if (applications.length > 0 && insurancePlans.length > 0 && !isLoaded) {
      const lastApplication = applications[applications.length - 1];
      if (lastApplication) {
        const transformedData = transformApplicationToOnboardingData(
          lastApplication as Application,
          insurancePlans
        );
        setOnboardingData(transformedData);

        setTimeout(() => {
          resetAllForms(transformedData);
        }, 100);
      }
      if (isLoaded && applications.length) {
        setCurrentStep(0);
      }
    }
  }, [applications, insurancePlans, setOnboardingData, isLoaded, resetAllForms, setCurrentStep]);

  // Enhance and update onboarding data when data, issues, or conditions change
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      const enhancedData = enhanceHealthQuestionsData(data, issues, conditions);
      resetAllForms(enhancedData);
    }
  }, [data, issues, conditions, enhanceHealthQuestionsData, resetAllForms]);
};